/* eslint-disable @typescript-eslint/no-explicit-any */
import { Chart } from 'chart.js';
import compactFormatterNumber from './compactFormatterNumber';
import formatCurrency from './formatCurrency';

export const CURRENT_REV_OPTIONS = {
  responsive: true,
  stacked: true,
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        usePointStyle: true,
      },
    },
  },
  interaction: {
    mode: 'index' as const,
    intersect: false,
  },
};

export const VALUATION_TREND = {
  responsive: true,
  stacked: false,
  tooltips: {
    enabled: false,
  },
  layout: {
    padding: {
      top: 40,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
      text: 'Valuation Trends',
    },
    tooltip: {
      enabled: false,
    },
    datalabels: {
      display: true,
      align: 'top' as const,
      font: {
        weight: 'bold' as const,
        size: 8,
      },
      formatter: (value: string) => formatCurrency({ value, currency: 'USD', maximumFractionDigits: 2 }),
      padding: 0,
    },
  },
  scales: {
    equity: {
      display: true,
      position: 'left' as const,
      ticks: {
        callback: (value: any) => {
          if (typeof value === 'number') {
            return `$${compactFormatterNumber.format(value)}`;
          }

          return value;
        },
      },
      grid: { display: false },
    },
    common: {
      display: true,
      position: 'right' as const,
      ticks: {
        callback: (value: any) => {
          if (typeof value === 'number') {
            return `$${compactFormatterNumber.format(value)}`;
          }

          return value;
        },
      },
      grid: { display: false },
    },
  },
  interaction: {
    mode: 'index' as const,
    intersect: false,
  },
};

export const VALUATION_TREND_PLUGINS = {
  id: 'CustomLegendPlugin',
  beforeDraw(chartData: Chart) {
    const originalLabels = Chart.defaults.plugins.legend.labels.generateLabels(chartData);
    const { ctx, chartArea } = chartData;

    originalLabels.forEach((label, index) => {
      const textLabel = label.text.toUpperCase();
      ctx.font = '16px Arial';
      ctx.fillStyle = label.fillStyle as string;

      if (index === 0) {
        ctx.fillText(textLabel, chartArea.left, chartArea.top - 20);
      } else {
        const rightLegendX = chartArea.right - ctx.measureText(textLabel).width;
        ctx.fillText(textLabel, rightLegendX, chartArea.top - 20);
      }
    });
  },
};

// Function to format the x-axis and y-axis labels
const tickCallback = (value: number | string) => {
  if (typeof value === 'number') {
    return `$${compactFormatterNumber.format(value)}`;
  }
  return value;
};

export const WATERFALL_OPTIONS = (xLabelsWithFormattedCurrency: string[]) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: {
      display: false,
      text: 'Waterfall Breakpoint Analysis',
    },
    legend: {
      position: 'bottom' as const,
      labels: {
        usePointStyle: true,
      },
    },
    tooltip: {
      // chance format the tooltip title
      callbacks: {
        title: (tooltipItem: any) => `${xLabelsWithFormattedCurrency[tooltipItem[0].dataIndex]}`,
        label: (tooltipItem: any) =>
          ` ${tooltipItem.dataset.label}: ${formatCurrency({ value: tooltipItem.parsed.y })}`,
      },
    },
  },
  scales: {
    y: {
      type: 'linear' as const,
      stacked: true,
      grid: { display: true },
      ticks: {
        callback: tickCallback,
        autoSkip: true,
        maxTicksLimit: 9,
      },
    },
    x: {
      type: 'linear' as const,
      labels: xLabelsWithFormattedCurrency,
      offset: false,
      // Added bounds: 'data' to remove the space between the last label and the end of the chart
      bounds: 'data' as const,
      ticks: {
        callback: tickCallback,
        autoSkip: true,
        maxTicksLimit: 9,
      },
    },
  },
  interaction: {
    mode: 'index' as const,
    intersect: false,
    includeInvisible: true,
  },
});

export const RATES_OF_CHANGE_OPTIONS = {
  responsive: true,
  stacked: true,
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        usePointStyle: true,
      },
    },
  },
  scales: {
    x: {
      ticks: {
        autoSkip: true,
        maxTicksLimit: 9,
      },
    },
  },
  interaction: {
    mode: 'index' as const,
    intersect: false,
  },
};
